import * as $ from 'jquery';
import { Observable, Subject, BehaviorSubject } from 'common/barrels/rx';
import { serialDisposable, disposableSubscription, disposable, Lazy } from 'common/barrels/core';

import { newGuid } from 'common/functions/guid';
import { undefinedNullOrEmpty } from 'common/functions/string';
import { isArrayOf } from 'common/functions/array';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import * as rxui from 'common/rxui/core';
import { Color } from 'common/color';
import { detectZoom } from 'common/functions/zoom';
import { registerBinding } from 'runtime/bindingengine';
import { Map } from 'immutable';
import * as moment from 'moment';
import * as path from 'shared/path';

import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import { RuntimeInfoRequested } from 'runtime/events/runtimeinforequested';
import { FileUploadComplete } from 'runtime/events/fileuploadcomplete';
import { FileUploadBadFileFormat } from 'runtime/events/fileuploadbadfileformat';
import { FileUploadError } from 'runtime/events/fileuploaderror';
import { FileUploadVirusDetected } from 'runtime/events/fileuploadvirusdetected';
import { FileUploadStartRequested } from 'runtime/events/fileuploadstartrequested';
import { FileUploadCancelRequested } from 'runtime/events/fileuploadcancelrequested';
import { FileUploadRegistered } from 'runtime/events/fileuploadregistered';
import { FileUploadUnregistered } from 'runtime/events/fileuploadunregistered';
import { CropRequested } from 'runtime/events/croprequested';
import { ImageProcessingOptions } from 'runtime/data/imageprocessingoptions';
import { BindingInfo } from 'runtime/metadata/bindinginfo';
import { UploadResult } from 'runtime/data/uploadresult';
import { menuGates } from 'runtime/controls/editableimagemenu';
import { blobFileName, normalizeImageForUpload } from 'common/functions/imagenormalization';

import 'runtime/styles/controls/editableimage.less';

var lrmap = {
    capture: 'EditableImageCapture',
    captureNotAvailable: 'EditableImageCaptureNotAvailable',
    upload: 'EditableImageUpload',
    download: 'EditableImageDownload',
    edit: 'EditableImageEdit',
    'delete': 'EditableImageDelete',
    takePicture: 'EditableImageTakePicture',
    cancelCapture: 'EditableImageCancel',
    acceptEdit: 'EditableImageAcceptEdit',
    cancelEdit: 'EditableImageCancelEdit',
    initializing: 'ControlInitializing',
    noSilverlight: 'SilverlightRequired',
    getSilverlight: 'InstallSilverlight',
    badImage: 'EditableImageBadImage',
    cancel: 'EditableImageCancel',
    stateUploading: 'FileUploadStateUploadingNoProgress',
    stateError: 'FileUploadStateError',
    stateVirus: 'FileUploadStateVirusDetected',
    stateLoading: 'EditableImageLoading'
};

const userMediaOptions = {
    audio: false,
    video: true
};

enum States { Empty, Initializating, Uploading, Error, Uploaded, Capture, Loading, Missing };

const cameraWidth = 640;
const cameraHeight = 480;

export class EditableImage extends Control {
    private fileContext: string;
    private transferId: string;
    private $menuIndicator: JQuery;
    private $editImage: JQuery;
    private $loadingContainer: JQuery;
    private state: BehaviorSubject<States>;
    private viewInfo: Lazy<ViewInfoRequested>;
    private fileIdValue: string;
    private id: string;

    // hax for now - fix up below
    private $layout: JQuery;
    private $centerContainer: JQuery;

    private fileFieldBindings = serialDisposable(null);
    private menuDisposable = serialDisposable(null);
    private captureDisposable = serialDisposable(null);

    private captureReady: boolean;
    private $capture: JQuery = null;
    private $message: JQuery;
    private $empty: JQuery;
    private $missing: JQuery;

    private thumbRequested = new Subject<{}>();

    // bindable properties
    private defaultFileName: string;
    private alwaysUseDefaultFileName: boolean;
    private convertImageFormat: boolean;
    private borderColor: string;
    private menuIconColor: string;
    private lockRatio: boolean;
    private autoCrop: boolean;
    private scale: number;
    private menuEnabled: boolean;
    private captureEnabled: boolean;
    private deleteEnabled: boolean;
    private normalizeOrientation: boolean;
    private maxImageWidth: number;
    private maxImageHeight: number;
    private uploadEnabled = new BehaviorSubject<boolean>(false);
    private applyZoom = new Subject<boolean>();
    private thumbScale = new Subject<number>();

    private enabledSubject = new Subject<boolean>();

    private emptyIcon = new BehaviorSubject<string>(null);

    private dialogSettings = {
        minWidth: 200,
        minHeight: 180,
        heightOffset: 101,
        widthOffset: 11,
        minImageWidth: 200,
        minImageHeight: 200,
        maxImageWidth: 640,
        maxImageHeight: 480
    };

    private stateMap = Map<States, JQuery[]>();

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.id = newGuid();

        this.$element.addClass('control-container editableimage').attr('id', this.id);

        this.applyTooltip(this.$element);

        this.$layout = $('<div />').addClass('editableimage-layout editableimage-bordercolor').appendTo(this.$element);
        this.$centerContainer = $('<div />').addClass('editableimage-centercontainer').appendTo(this.$layout);
        var $initializing = $('<span />').addClass('editableimage-initializing').text(this.lr('initializing')).appendTo(this.$centerContainer);
        this.$message = $('<span />').addClass('editableimage-message').appendTo(this.$centerContainer).hide();
        this.$empty = $('<i />').addClass('fa editableimage-emptyicon editableimage-iconcolor').appendTo(this.$centerContainer).bindText(this.emptyIcon.map(x => x ? this.icon(x) : '')).hide();
        this.$missing = $('<i />').addClass('fa fa-ban editableimage-emptyicon editableimage-iconcolor').appendTo(this.$centerContainer).hide();
        this.$menuIndicator = $('<i />').appendTo(this.$element).addClass('fa fa-cog editableimage-menuicon editableimage-iconcolor').hide();

        this.viewInfo = new Lazy(() => this.eventAggregator.publish(new ViewInfoRequested()));

        const stateMap = this.stateMap.asMutable();

        stateMap.set(States.Initializating, [$initializing, this.$centerContainer]);
        stateMap.set(States.Empty, [this.$empty, this.$centerContainer]);
        stateMap.set(States.Uploading, [this.$message, this.$centerContainer]);
        stateMap.set(States.Uploaded, []);
        stateMap.set(States.Capture, []);
        stateMap.set(States.Error, [this.$message, this.$centerContainer]);
        stateMap.set(States.Loading, [this.$message, this.$centerContainer]);
        stateMap.set(States.Missing, [this.$missing, this.$centerContainer]);

        this.stateMap = stateMap.asImmutable();

        this.applyConfigNamesake(
            'content.AlwaysUserDefaultFileName', 'content.DefaultFileName', 'content.ConvertImageFormat', 'content.IconColor', 'content.BorderColor',
            'content.MenuIconColor', 'content.EmptyIcon', 'content.LockRatio', 'content.AutoCrop', 'content.Scale', 'content.MenuEnabled',
            'content.CaptureEnabled', 'content.UploadEnabled', 'content.DeleteEnabled',
            'content.NormalizeOrientation', 'content.MaxImageWidth', 'content.MaxImageHeight'
        );

        this.state = new BehaviorSubject(States.Initializating);
        var transitions = this.state.distinctUntilChanged().pairwise();

        var click = rxui.click(this.$layout).merge(rxui.click(this.$menuIndicator)).filter(x => this.menuEnabled === true).withLatestFrom(this.state.asObservable(), (x, y) => y).share();

        this.subscriptions = disposableSubscription(
            this.state,
            this.state.pairwise().subscribe(x => {
                this.stateMap.get(x[0]).forEach(x => x.hide());
                this.stateMap.get(x[1]).forEach(x => x.show());
            }),
            rxui.hover(this.$element).filter(x => this.menuEnabled === true).combineLatest(this.enabledSubject,(x, y) => x && y).subscribe(x => x ? this.$menuIndicator.show() : this.$menuIndicator.hide()),
            click.filter(x => x === States.Empty || x === States.Uploaded).subscribe(() => this.mainMenu()),
            click.filter(x => x === States.Missing).subscribe(() => this.missingMenu()),
            click.filter(x => x === States.Uploading).subscribe(() => this.uploadingMenu()),
            click.filter(x => x === States.Capture).subscribe(() => this.captureMenu()),
            click.filter(x => x === States.Error).subscribe(() => this.errorMenu()),
            this.eventAggregator.query(FileUploadComplete).filter(x => x.transferId === this.transferId).subscribe(x => this.uploadComplete(x.fileId, x.newName, x.newLength)),
            this.eventAggregator.query(FileUploadBadFileFormat).filter(x => x.transferId === this.transferId).subscribe(() => this.badFormat()),
            this.eventAggregator.query(FileUploadError).filter(x => x.transferId === this.transferId).subscribe(() => this.uploadError()),
            this.eventAggregator.query(FileUploadVirusDetected).filter(x => x.transferId === this.transferId).subscribe(() => this.virusDetected()),
            this.uploadEnabled
                .combineLatest(this.enabledSubject, (x, y) => x && y)
                .distinctUntilChanged().switchMap(x => x == true ? rxui.enableDragAndDrop(this.$element) : Observable.never()).subscribe(e => this.performUpload(e)),
            transitions.filter(x => x[0] === States.Capture && x[1] !== States.Uploading).subscribe(() => this.captureDisposable.set(null)),
            transitions.filter(x => x[0] === States.Uploading).subscribe(() => this.captureDisposable.set(null)),
            transitions.filter(x => x[0] === States.Capture && x[1] === States.Uploading).subscribe(() => this.$capture.parent().addClass('invisible-but-not-hidden')),

            this.thumbRequested
                .combineLatest(this.applyZoom.distinctUntilChanged(), this.thumbScale.distinctUntilChanged(), (_, applyZoom, thumbScale) => ({ applyZoom, thumbScale }))
                .subscribe(({ applyZoom, thumbScale }) => this.showThumb(applyZoom, thumbScale)),

            this.processBindings(this.config),
            this.managePrimaryBinding('content.FileField')
        );

        if (!this.hasBinding('content.ApplyZoom'))
            this.applyConfigNamesake('content.ApplyZoom');

        if (!this.hasBinding('content.ThumbScale'))
            this.applyConfigNamesake('content.ThumbScale');

        this.initialize();
    }

    private transitioned(states: States[], before: States, after: States) {
        return states[0] === before && states[1] === after;
    }

    dispose() {
        super.dispose();
        this.fileFieldBindings.dispose();
        this.menuDisposable.dispose();
    }

    private async performUpload(event) {
        this.closeMenu();

        var source = event.target instanceof HTMLInputElement ? event.target : event.dataTransfer;

        var file = source.files !== undefined ? source.files[0] : null;

        var transferId = newGuid();
        this.transferId = transferId;
        this.uploading(); // immediate feedback while a large photo normalizes

        var uploadSource: any = source;
        var fileName = file ? file.name : path.fileName(source.value);
        var fileSize = file ? file.size : null;

        if (file) {
            const normalized = await normalizeImageForUpload(file, {
                normalizeOrientation: this.normalizeOrientation,
                maxWidth: this.maxImageWidth,
                maxHeight: this.maxImageHeight,
                quality: 0.9,
                convertFormat: this.convertImageFormat
            });

            if (normalized !== file) {
                uploadSource = normalized;   // a Blob; uploadmanager names it from its type
                fileName = blobFileName(normalized.type);
                fileSize = normalized.size;
            }
        }

        if (this.disposed || this.transferId !== transferId) return; // disposed, cancelled, or superseded by a newer upload during normalization

        this.$element.triggerHandler('fileName', fileName);
        this.$element.triggerHandler('fileSize', fileSize);
        this.$element.triggerHandler('uploadUserId', this.viewInfo.value().currentUserId);
        this.$element.triggerHandler('uploadWorkItemId', this.viewInfo.value().workItemId);
        this.$element.triggerHandler('uploadDate', moment.utc().toISOString());

        this.eventAggregator.publish(
            new FileUploadStartRequested(transferId, uploadSource, this.getImageProcessingOptions(), this.alwaysUseDefaultFileName ? this.defaultFileName : null)
        );
    }

    private getImageProcessingOptions() {
        return new ImageProcessingOptions(
            this.convertImageFormat,
            this.scale,
            this.autoCrop ? this.$element.width() : null,
            this.autoCrop ? this.$element.height() : null
        );
    }

    private uploadComplete(fileId, name, length) {
        this.$element.triggerHandler('fileId', fileId);
        this.$element.triggerHandler('fileName', name);
        this.$element.triggerHandler('fileSize', length);
        this.closeMenu();
    }

    private edit() {
        this.closeMenu();

        if (!this.$loadingContainer)
            this.$loadingContainer = $('<div />').appendTo(this.$element).addClass('editableimage-loadingcontainer');

        this.$editImage = $('<img />')
            .addClass('editableimage-hiddenimage')
            .one('load', () => {
                this.showEditDialog(this.$editImage);
                this.$editImage.removeClass('editableimage-hiddenimage');
            })
            .appendTo(this.$loadingContainer);

        this.$editImage.attr('src', path.fullImage(this.fileIdValue, this.viewInfo.value().workItemId));
    }

    private showEditDialog($image) {
        var imageHeight = +('naturalHeight' in $image[0] ? $image[0].naturalHeight : $image.height());
        var imageWidth = +('naturalWidth' in $image[0] ? $image[0].naturalWidth : $image.width());
        var targetAspectRatio = this.width() / this.height();
        var imageAspectRatio = imageWidth / imageHeight;

        var minScale = Math.max(
            imageHeight >= this.dialogSettings.minImageHeight ? 1 : this.dialogSettings.minImageHeight / imageHeight,
            imageWidth >= this.dialogSettings.minImageWidth ? 1 : this.dialogSettings.minImageWidth / imageWidth
        );

        var scale = Math.min(
            imageHeight * minScale <= this.dialogSettings.maxImageHeight ? minScale : this.dialogSettings.maxImageHeight / imageHeight,
            imageWidth * minScale <= this.dialogSettings.maxImageWidth ? minScale : this.dialogSettings.maxImageWidth / imageWidth
        );

        var imageSelector = null;

        var $dialogContent = $('<div />')
            .addClass('editableimage-dialog')
            .append($image.height(Math.round(imageHeight * scale)).width(Math.round(imageWidth * scale)))
            .dialog({
                appendTo: '.runtime-application-container',
                modal: true,
                height: $image.height() + this.dialogSettings.heightOffset,
                width: $image.width() + this.dialogSettings.widthOffset,
                minHeight: this.dialogSettings.minHeight,
                minWidth: this.dialogSettings.minWidth,
                buttons: [
                    {
                        text: this.lr('acceptEdit'),
                        click: () => {
                            this.crop(imageWidth, imageHeight, imageSelector);
                            $dialogContent.dialog('close');
                        }
                    },
                    { text: this.lr('cancelEdit'), click: () => $dialogContent.dialog('close') }
                ],
                close: () =>$dialogContent.remove()
            });

        var initialSelection = {
            x1: (targetAspectRatio >= imageAspectRatio ? 0 : imageWidth / 2 - (imageHeight * targetAspectRatio) / 2),
            y1: 0,
            x2: (targetAspectRatio >= imageAspectRatio ? imageWidth : imageWidth / 2 + (imageHeight * targetAspectRatio) / 2),
            y2: (targetAspectRatio <= imageAspectRatio ? imageHeight : imageWidth / targetAspectRatio)
        };

        imageSelector = $image.imgAreaSelect($.extend({}, initialSelection, {
            parent: $dialogContent,
            handles: true,
            instance: true,
            aspectRatio: this.lockRatio ? (this.width().toString() + ':' + this.height().toString()) : undefined,
            imageHeight: imageHeight,
            imageWidth: imageWidth,
            persistent: true,
            onSelectEnd: (img, selection) => {
                if (selection.height === 0 || selection.width === 0)
                    this.setSelection(imageSelector, initialSelection);
            }
        }));
    }

    private crop(imageWidth: number, imageHeight: number, imageSelector: any) {
        var selection = imageSelector.getSelection();
        this.eventAggregator.publish(
            new CropRequested(this.fileIdValue, this.viewInfo.value().workItemId, imageWidth, imageHeight, selection.x1, selection.y1, selection.width, selection.height, x => {
                this.uploadComplete(x.fileId, x.fileName, x.length);
            })
        );
    }

    private setSelection (imageSelector, selection) {
        imageSelector.setSelection(selection.x1, selection.y1, selection.x2, selection.y2);
        imageSelector.update();
        imageSelector.setOptions({ show: true });
    }

    private abandonTransfer() {
        var abandonedTransferId = this.transferId;

        this.transferId = null;

        this.closeMenu();

        if (!!abandonedTransferId)
            this.eventAggregator.publish(new FileUploadCancelRequested(abandonedTransferId));
    }

    private clear() {
        this.abandonTransfer();

        this.$element.triggerHandler('fileName', [null]);
        this.$element.triggerHandler('fileSize', [null]);
        this.$element.triggerHandler('uploadUserId', [null]);
        this.$element.triggerHandler('uploadWorkItemId', [null]);
        this.$element.triggerHandler('uploadDate', [null]);
        this.$element.triggerHandler('fileId', [null]);

        this.state.next(States.Empty);
    }

    private dismissError() {
        this.abandonTransfer();

        this.pictureMode();
    }

    private userMediaAvailable() {
        const nav = (<any>navigator);
        return !!nav.mediaDevices && !!nav.mediaDevices.getUserMedia;
    }

    private captureMode() {
        if (!this.userMediaAvailable()) {
            this.captureNotAvailable();
            return;
        }
        
        this.captureReady = false;
        this.setState(States.Capture);
        this.captureMenu();

        var $captureContainer = $('<div />').appendTo(this.$layout).addClass('editableimage-capturecontainer');

        var $capture = this.htmlCapture($captureContainer);

        this.captureDisposable.set(disposable(() => {
            $captureContainer.remove();
            this.$capture = null;
        }));

        this.$capture = $capture;
    }

    private htmlCapture($captureContainer: JQuery) {
        const $capture = $('<div />').appendTo($captureContainer);
        const $video = $('<video />').appendTo($capture);

        const success = stream => {
            const video = <any>$video[0];
            video.srcObject = stream;
            video.play();

            const width = $captureContainer.width();
            const height = $captureContainer.height();
            const scale = this.calculateScale(width, height, cameraWidth, cameraHeight);
            const translateX = (-cameraWidth / 2) + (width / 2 * scale);

            $capture
                .css('transform-origin', 'top left')
                .css('transform', `scale(${1 / scale}) translateX(${translateX}px)`)
                .width(width * scale)
                .height(Math.ceil(height * scale))
                ;

            $video.on('remove', () => stream.getTracks().forEach(t => t.stop()));

            this.captureReady = true;
        };

        const failure = () => {
            this.safe(() => this.captureNotAvailable())();
        };

        const options = $.extend({}, userMediaOptions, {
            video: {
                width: { exact: cameraWidth },
                height: { exact: cameraHeight }
            }
        });

        (<any>navigator).mediaDevices.getUserMedia(options).then(success).catch(failure);

        return $capture;
    }

    private calculateScale(imageWidth, imageHeight, controlWidth, controlHeight) {
        const targetAspectRatio = controlWidth / controlHeight;
        const imageAspectRatio = imageWidth / imageHeight;

        const minScale = Math.max(
            imageHeight >= controlHeight ? 1 : controlHeight / imageHeight,
            imageWidth >= controlWidth ? 1 : controlWidth / imageWidth
        );

        const scale = Math.min(
            imageHeight * minScale <= controlHeight ? minScale : controlHeight / imageHeight,
            imageWidth * minScale <= controlWidth ? minScale : controlWidth / imageWidth
        );

        return scale;
    }

    private safe(lambda: () => void) {
        return () => {
            if (!this.disposed)
                lambda();
        };
    }

    takePicture() {
        if (!this.captureReady) {
            this.captureError();
            return;
        }

        this.uploading();

        this.transferId = newGuid();

        if (this.userMediaAvailable())
            this.takePictureHtml();
        else
            this.takePictureSilverlight();
    }

    private takePictureSilverlight() {
        (<any>this.$capture[0]).Content.ScriptableMembers.Capture();
        this.eventAggregator.publish(new FileUploadRegistered(this.transferId));
    }

    private blobFromHtmlCapture(callback: (b: Blob) => void) {
        const canvas = <any>document.createElement('canvas');
        const $source = $(this.$capture.find('video')[0]);
        const width = Math.min($source.parent().width(), $source.width());
        const height = Math.min($source.parent().height(), $source.height());

        canvas.width = width;
        canvas.height = height;

        // this should probably actually be solved by reimplementing the border as a floating rectangle with a border so it doesn't "size-down" this contained element
        const w = this.width() - 2;
        const h = this.height() - 2;

        const scale = this.calculateScale(w, h, cameraWidth, cameraHeight);
        const offset = Math.round((cameraWidth / 2) - (w / 2 * scale));

        canvas.getContext('2d').drawImage($source[0], offset, 0, width, height, 0, 0, width, height);
        if (canvas.msToBlob) {
            callback(canvas.msToBlob());
        }
        else {
            canvas.toBlob((blob: Blob) => callback(blob));
        }
    }

    private takePictureHtml() {
        this.blobFromHtmlCapture(blob => {
            this.eventAggregator.publish(
                new FileUploadStartRequested(this.transferId, blob, this.getImageProcessingOptions(), this.alwaysUseDefaultFileName ? this.defaultFileName : null)
            );
        });
    }

    private captureComplete(rawResult) {
        this.closeMenu();

        var result = this.serializer.read(typeof rawResult === 'string' ? JSON.parse(rawResult) : rawResult);
        if (isArrayOf(UploadResult, result)) {
            var successResult = <UploadResult>result[0];
            this.uploadComplete(successResult.fileId, successResult.fileName, successResult.length);
            this.setState(States.Uploaded);
            this.eventAggregator.publish(new FileUploadUnregistered(this.transferId));
        } else {
            this.captureError();
        }
    }

    cancelCapture() {
        this.closeMenu();
        this.pictureMode();
    }

    // menu routines
    private menu(items: MenuItem[], cancelAction: () => void) {
        if (items.length === 0 || !this.menuEnabled) {
            this.menuDisposable.set(null);
            return;
        }

        var makeLink = (action) => action instanceof Function
            ? $('<a />').on('click', e => { action(); e.stopPropagation(); })
            : $('<a />').on('click', () => this.closeMenu()).attr('href', action);
        
        var makeDisabledLink = () => $('<a />').addClass('editableimage-menu-disabled').on('click', e => { e.stopPropagation(); });

        var makeContent = (x: MenuItem) => $('<span />').text(x.text).append($('<i />').addClass('fa fa-' + x.icon).css('color', Color.splitArgb(this.menuIconColor)[0]));
        var makeItem = (x: MenuItem) => $('<li />')
            .append(!x.disabled ? makeLink(x.action).append(makeContent(x)) : makeDisabledLink().append(makeContent(x)))
            .append(x.$extra);

        var $menu = $('<div />')
            .addClass('editableimage-menu')
            .css('width', this.$element.width())
            .css('color', this.borderColor)
            .append(items.reduce((a, x) => (a.append(makeItem(x)), a), $('<ul />')))

            .appendTo(this.$element.closest('.runtime-application-container'))
            .position({
                my: 'left top',
                at: 'left bottom',
                of: this.$element,
                collision: 'flipfit'
            });

        this.menuDisposable.set(disposableSubscription(
            Observable.interval(100).filter(() => !this.$element.is(':visible')).first().subscribe(() => (cancelAction || this.closeMenu)()),
            disposable(() => $menu.remove()),
            rxui.clickOutside(this.$element.add($menu)).subscribe(() => this.closeMenu())
        ));
    }

    private mainMenu() {
        var gates = this.currentMenuGates();

        this.menu([]
            .concat(gates.capture ? [new MenuItem(this.lr('capture'), 'camera', () => this.captureMode())] : [])
            .concat(gates.captureNotAvailable ? [new MenuItem(this.lr('captureNotAvailable'), 'camera', () => {}, undefined, true)] : [])
            .concat(gates.upload ? [this.uploadMenuItem()] : [])
            .concat(gates.download ? [new MenuItem(this.lr('download'), 'download', path.download(this.fileIdValue, this.viewInfo.value().workItemId))] : [])
            .concat(gates.edit ? [new MenuItem(this.lr('edit'), 'edit', () => this.edit())] : [])
            .concat(gates.delete ? [new MenuItem(this.lr('delete'), 'remove', () => this.clear())] : []), null);
    }

    private currentMenuGates() {
        return menuGates({
            enabled: this.derivedEnabledState() !== false,
            captureEnabled: this.captureEnabled,
            uploadEnabled: this.uploadEnabled.getValue(),
            deleteEnabled: this.deleteEnabled,
            hasFile: this.fileIdValue !== null,
            userMediaAvailable: this.userMediaAvailable()
        });
    }

    private uploadMenuItem() {
        return new MenuItem(this.lr('upload'), 'upload', () => { }, $('<label />').append('<input type="file" accept="image/*" />').on('change', e => this.performUpload(e)));
    }

    private captureMenu() {
        this.menu([
            new MenuItem(this.lr('takePicture'), 'camera', () => this.takePicture()),
            new MenuItem(this.lr('cancelCapture'), 'arrow-circle-left', () => this.cancelCapture())
        ], () => this.cancelCapture());
    }

    private uploadingMenu() {
        this.menu([
            new MenuItem(this.lr('cancel'), 'remove',() => this.clear())
        ], () => this.closeMenu());
    }

    private errorMenu() {
        // Cancelling a failed operation reverts to the last good state (dismissError), never deletes
        // the existing image - so a re-upload that errors over a committed photo leaves it intact.
        this.menu([new MenuItem(this.lr('cancel'), 'remove', () => this.dismissError())], () => this.closeMenu());
    }

    private missingMenu() {
        var gates = this.currentMenuGates();

        this.menu([]
            .concat(gates.capture ? [new MenuItem(this.lr('capture'), 'camera', () => this.captureMode())] : [])
            .concat(gates.captureNotAvailable ? [new MenuItem(this.lr('captureNotAvailable'), 'camera', () => {}, undefined, true)] : [])
            .concat(gates.upload ? [this.uploadMenuItem()] : [])
            .concat(gates.delete ? [new MenuItem(this.lr('delete'), 'remove', () => this.clear())] : []), null);
    }

    private closeMenu() { this.menuDisposable.set(null); }

    // bindable properties
    'content.FileField'(value) {
        this.fileContext = value;

        this.fileFieldBindings.set(
            disposableSubscription(
                this.values.createBinding(new BindingInfo('Field', 'FileId', 'fileId', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'FileName', 'fileName', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'FileSize', 'fileSize', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'UploadUserId', 'uploadUserId', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'UploadWorkItemId', 'uploadWorkItemId', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'UploadDate', 'uploadDate', 'TwoWay', null), this, this.$element, this.fileContext)
            )
        );

        this.checkInitialValueReceived();
    }

    'content.AlwaysUserDefaultFileName'(value: boolean) {
        this.alwaysUseDefaultFileName = value;
    }

    'content.DefaultFileName'(value: string) {
        this.defaultFileName = undefinedNullOrEmpty(value) ? 'image.jpg' : path.getFileNameWithoutExtension(value);
    }

    'content.ConvertImageFormat'(value: boolean) {
        this.convertImageFormat = value === null ? true : value;
    }

    'content.IconColor'(value: string) {
        this.$element.find('.editableimage-iconcolor').css('color', this.colorOrDefault(value, 'content.IconColor'));
    }

    'content.BorderColor'(value: string) {
        this.borderColor = this.colorOrDefault(value, 'content.BorderColor');

        this.$element.find('.editableimage-bordercolor').css('color', this.borderColor);
    }

    private colorOrDefault(value: string, configProp: string) {
        return Color.splitArgb(undefinedNullOrEmpty(value) ? this.config[configProp] : value)[0]
    }

    'content.MenuIconColor'(value: string) {
        this.menuIconColor = undefinedNullOrEmpty(value) ? this.config['content.MenuIconColor']
            : value;
    }

    'content.EmptyIcon'(value: string) {
        this.emptyIcon.next(value);
    }

    'content.LockRatio'(value: boolean) {
        this.lockRatio = value !== null ? value : this.config['content.LockRatio'];
    }

    'content.AutoCrop'(value: boolean) {
        this.autoCrop = value !== null ? value : this.config['content.AutoCrop'];
    }

    'content.Scale'(value: boolean) {
        this.scale = value !== null ? value : this.config['content.Scale'];
    }

    'content.MenuEnabled'(value: boolean) {
        this.menuEnabled = value !== null ? value : this.config['content.MenuEnabled'];
    }

    'content.CaptureEnabled'(value: boolean) {
        this.captureEnabled = value !== null ? value : this.config['content.CaptureEnabled'];
    }

    'content.DeleteEnabled'(value: boolean) {
        this.deleteEnabled = value !== null ? value : this.config['content.DeleteEnabled'];
    }

    'content.NormalizeOrientation'(value: boolean) {
        this.normalizeOrientation = value !== false; // absent/null/undefined => on (menuGates !== false convention)
    }

    'content.MaxImageWidth'(value: number) {
        this.maxImageWidth = value == null ? 0 : value; // 0 disables resize; absent/cleared => 0 (resize off by default)
    }

    'content.MaxImageHeight'(value: number) {
        this.maxImageHeight = value == null ? 0 : value;
    }

    'content.UploadEnabled'(value: boolean) {
        this.uploadEnabled.next(value !== null ? value : this.config['content.UploadEnabled']);
    }

    'content.ApplyZoom'(value: boolean) {
        this.applyZoom.next(value !== null ? value : this.config['content.ApplyZoom']);
    }

    'content.ThumbScale'(value: number) {
        this.thumbScale.next(value !== null ? value : this.config['content.ThumbScale']);
    }

    fileId(value) {
        if (this.fileIdValue === undefined && value === null) {
            this.fileIdValue = null;
            this.setState(States.Empty);
            return;
        }

        if (this.fileIdValue === value)
            return;

        this.fileIdValue = value;

        var url = path.sizedImage(value, this.viewInfo.value().workItemId, this.$element.width(), this.$element.height());

        if (this.fileIdValue === null) {
            this.clear();
        } else {
            this.loading();
            this.thumbRequested.next({});
        }
    }

    private showThumb(applyZoom: boolean, thumbScale: number) {
        this.$layout.find('img').remove();

        var $img = $('<img>').appendTo(this.$layout)
            .one('load', () => this.thumbLoaded($img))
            .one('error', () => this.missing())
            .hide();

        this.loading();

        const zoomScale = applyZoom === true ? detectZoom().zoom : 1;
        const specifiedScale = (thumbScale === undefined || thumbScale === null) ? 1 : thumbScale;

        const thumbnailDimensionFactor = zoomScale * specifiedScale;

        $img.attr('src', path.sizedImage(
            this.fileIdValue,
            this.viewInfo.value().workItemId,
            Math.round(this.$element.width() * thumbnailDimensionFactor),
            Math.round(this.$element.height() * thumbnailDimensionFactor)
        ));
    }

    private thumbLoaded($img: JQuery) {
        var img = <HTMLImageElement>$img.get(0);

        this.stateMap = this.stateMap.set(States.Uploaded, [$img]);

        this.setState(States.Uploaded);
    }


    uploadUserId(value) { }
    uploadDate(value) { }
    uploadWorkItemId(value) { }
    fileSize(value) { }
    fileName(value) { }

    focus() { }

    applyEnabledState(value: boolean) {
        this.enabledSubject.next(value);
    }

    activateCamera() {
        this.captureMode();
    }

    // error-handling routines
    private uploadError() {
        this.error('stateError');

        if (this.hasSubscriptions('EditableImageFileUploadError'))
            this.triggerEvent('EditableImageFileUploadError');
    }

    private virusDetected() {
        this.error('stateVirus');

        if (this.hasSubscriptions('EditableImageVirusDetected'))
            this.triggerEvent('EditableImageVirusDetected');
    }

    private badFormat() {
        this.error('badImage');

        if (this.hasSubscriptions('EditableImageBadImage'))
            this.triggerEvent('EditableImageBadImage');
    }

    private captureNotAvailable() {
        this.error('stateError');

        if (this.hasSubscriptions('EditableImageCameraCaptureNotAvailable'))
            this.triggerEvent('EditableImageCameraCaptureNotAvailable');
        else
            this.methods.invoke('Document', 'cameraCaptureNotAvailable', null,() => { });
    }

    private captureError() {
        this.error('stateError');

        if (this.hasSubscriptions('EditableImageCameraCaptureError'))
            this.triggerEvent('EditableImageCameraCaptureError');
        else
            this.methods.invoke('Document', 'cameraCaptureError', null,() => { });
    }

    // utility routines
    private pictureMode() {
        this.setState(this.fileIdValue === null ? States.Empty : States.Uploaded);
    }

    private setState(state: States) {
        this.state.next(state);
    }

    private error(resource: string) {
        this.$message.text(this.lr(resource || 'error'));
        this.setState(States.Error);
        this.errorMenu();
    }

    private uploading() {
        this.$message.text(this.lr('stateUploading'));
        this.setState(States.Uploading);
        this.uploadingMenu();
    }

    private loading() {
        this.$message.text(this.lr('stateLoading'));
        this.setState(States.Loading);
        this.closeMenu();
    }

    private missing() {
        this.setState(States.Missing);
        this.closeMenu();
    }

    private height() {
        return this.config['elementHeight'];
    }

    private width() {
        return this.config['elementWidth'];
    }

    private icon(c) {
        return String.fromCharCode(+('0x' + (c || '').toLowerCase()));
    }

    private lr(moniker: string) {
        return this.resources.getCoreString(lrmap[moniker]);
    }
}

registerBinding('editableimage', EditableImage);

class MenuItem {
    constructor(public text: string, public icon: string, public action: (() => void)|string, public $extra?: JQuery, public disabled: boolean = false) { }
}